import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import {
  PRACTICES,
  STATS,
  HERO_VIDEO,
  HERO_VIDEO_FALLBACK,
  HERO_POSTER,
  PRIMARY_CTA,
  CONSULTATION_CTA,
  IMAGES,
  RELOCATION_SERVICES,
} from "@/lib/data";

const Hero = () => (
  <section data-testid="home-hero" className="relative w-full min-h-[92vh] flex items-end overflow-hidden bg-[#000000]">
    <video data-testid="hero-video" autoPlay loop muted playsInline poster={HERO_POSTER} className="absolute inset-0 w-full h-full object-cover">
      <source src={HERO_VIDEO} type="video/mp4" />
      <source src={HERO_VIDEO_FALLBACK} type="video/mp4" />
    </video>
    <div className="absolute inset-0 hero-overlay" />

    <div className="container-kc relative z-10 pb-24 pt-40 w-full">
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl">
        <div className="flex items-center gap-4 mb-8">
          <span className="hairline--gold" />
          <span className="text-[11px] tracking-[0.22em] uppercase text-[#C5A880] font-semibold">
            Kassim Consulting · Washington, DC
          </span>
        </div>
        <h1 data-testid="hero-headline" className="font-serif text-white text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-medium">
          Relocation with Dignity. <span className="text-[#C5A880]">Compliance with Precision.</span>
        </h1>
        <p data-testid="hero-subhead" className="text-white/85 mt-8 max-w-2xl text-base md:text-lg leading-relaxed">
          A Washington, DC consulting firm delivering URA, HUD 1378, RAD, and Section 8 compliant resident relocation for affordable and mixed-use housing.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link to={PRIMARY_CTA.to} data-testid="hero-cta-primary" className="btn btn--primary" style={{ background: "#ffffff", color: "#000000" }}>
            {PRIMARY_CTA.label} <span className="btn-arrow">→</span>
          </Link>
          <Link
            to={CONSULTATION_CTA.to}
            data-testid="hero-cta-consultation"
            className="inline-flex items-center gap-2 px-5 py-3 border border-[#C5A880] text-white hover:bg-[#C5A880] hover:text-[#000000] text-[12px] tracking-[0.18em] uppercase font-semibold transition-colors"
          >
            {CONSULTATION_CTA.label}
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const OurPractice = () => {
  const p = PRACTICES[0];
  return (
    <section className="section bg-white" data-testid="our-practice">
      <div className="container-kc">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline--gold" />
                <span className="eyebrow">Our Practice</span>
              </div>
              <h2 className="h-display text-3xl md:text-5xl">
                Compliant resident relocation, done right.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                Kassim Consulting delivers federally regulated resident relocation for affordable and
                mixed-use housing. From resident outreach to post-move stabilization, we keep projects on
                schedule and communities respected.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-gray-200">
            <div className="bg-white md:col-span-7">
              <div className="aspect-[16/10] md:aspect-auto md:h-full overflow-hidden bg-gray-100">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className="bg-white md:col-span-5 p-10 lg:p-14 flex flex-col" data-testid="practice-card-relocation">
              <div className="kc-index mb-4">{p.eyebrow}</div>
              <h3 className="font-serif text-3xl md:text-4xl text-[#000000] leading-snug">{p.title}</h3>
              <p className="text-gray-600 mt-5 leading-relaxed text-[15px]">{p.long}</p>
              <div className="flex-1" />
              <Link
                to={`/${p.slug}`}
                data-testid="practice-cta-relocation"
                className="mt-10 inline-flex items-center gap-2 text-[13px] tracking-[0.18em] uppercase font-semibold text-[#000000] hover:text-[#C5A880] transition-colors group self-start border-b border-[#000000] hover:border-[#C5A880] pb-1"
              >
                {p.cta}
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ServicesPreview = () => (
  <section className="bg-[#F8F9FA]" data-testid="services-preview">
    <div className="container-kc py-20 md:py-28">
      <Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline--gold" />
              <span className="eyebrow">What We Do</span>
            </div>
            <h2 className="h-display text-3xl md:text-5xl">
              From outreach to post-move stabilization.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="text-gray-600 text-base leading-relaxed">
              End-to-end relocation delivered by senior practitioners. URA, HUD, RAD, and Section 8 compliant.
            </p>
          </div>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
        {RELOCATION_SERVICES.map((s, idx) => (
          <Reveal key={s.title} delay={idx * 0.04}>
            <div className="bg-white p-8 lg:p-10 h-full flex flex-col" data-testid={`home-service-${idx}`}>
              <div className="kc-index mb-4">0{idx + 1}</div>
              <h3 className="font-serif text-xl md:text-2xl text-[#000000] leading-snug">{s.title}</h3>
              <p className="text-gray-600 mt-4 text-[14px] leading-relaxed">{s.description}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-12 flex justify-center">
          <Link to="/relocation" className="btn btn--secondary" data-testid="home-services-cta">
            See the Full Practice <span className="btn-arrow">→</span>
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const StatBand = () => (
  <section className="bg-[#000000] text-white" data-testid="stat-band">
    <div className="container-kc py-20 md:py-24">
      <Reveal>
        <div className="flex items-center gap-3 mb-12">
          <span className="hairline--gold" />
          <span className="eyebrow eyebrow--gold">Our Work in Numbers</span>
        </div>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/15">
        {STATS.map((s, idx) => (
          <Reveal key={s.label} delay={idx * 0.06}>
            <div data-testid={`stat-${idx}`} className="bg-[#000000] p-8 lg:p-10 h-full">
              <div className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-none">{s.value}</div>
              <div className="hairline--gold mt-6 mb-4" />
              <div className="text-xs md:text-sm uppercase tracking-[0.18em] text-white/70 leading-relaxed">{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ClosingCTA = () => (
  <section className="bg-white border-t border-gray-100" data-testid="closing-cta">
    <div className="container-kc py-24 md:py-32">
      <Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline--gold" />
              <span className="eyebrow">Start a Conversation</span>
            </div>
            <h2 className="h-display text-3xl md:text-6xl max-w-3xl">
              Let&apos;s build what&apos;s next.
            </h2>
            <p className="text-gray-600 mt-6 max-w-xl">
              Tell us about your project and we will get back to you.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end flex-wrap gap-3">
            <Link to={CONSULTATION_CTA.to} className="inline-flex items-center gap-2 px-5 py-3 border border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-white text-[12px] tracking-[0.18em] uppercase font-semibold transition-colors" data-testid="closing-cta-consultation">
              {CONSULTATION_CTA.label}
            </Link>
            <Link to={PRIMARY_CTA.to} className="btn btn--primary" data-testid="closing-cta-button">
              {PRIMARY_CTA.label} <span className="btn-arrow">→</span>
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  return (
    <main data-testid="page-home">
      <Hero />
      <OurPractice />
      <ServicesPreview />
      <StatBand />
      <ClosingCTA />
    </main>
  );
};

export default Home;
